import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { pipelinesApi } from '../../lib/resources';
import { setUiPrefs, useUiPrefs } from '../../lib/ui-prefs';
import type { Pipeline } from '../../types';
import { inputClass } from '../ui';

const NO_GROUP = 'Sem grupo';

/**
 * Barra lateral de pipelines: Favoritas no topo, grupos em acordeão, busca e
 * arrastar para reordenar (dentro do grupo ou levando para outro grupo).
 */
export function PipelineSidebar({
  pipelines,
  activeId,
  canManage,
  onSelect,
  onCreate,
}: {
  pipelines: Pipeline[];
  activeId: string | null;
  canManage: boolean;
  onSelect: (id: string) => void;
  onCreate: () => void;
}) {
  const queryClient = useQueryClient();
  const hidden = useUiPrefs().pipelinesCollapsed;
  const [term, setTerm] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [dragging, setDragging] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['pipelines'] });
  const favorite = useMutation({
    mutationFn: ({ id, on }: { id: string; on: boolean }) => pipelinesApi.favorite(id, on),
    onSuccess: refresh,
  });
  const reorder = useMutation({
    mutationFn: pipelinesApi.reorder,
    onSuccess: refresh,
  });

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase();
    return needle
      ? pipelines.filter((pipeline) =>
          `${pipeline.name} ${pipeline.group ?? ''}`.toLowerCase().includes(needle),
        )
      : pipelines;
  }, [pipelines, term]);

  const groups = useMemo(() => {
    const map = new Map<string, Pipeline[]>();
    for (const pipeline of filtered) {
      const key = pipeline.group ?? NO_GROUP;
      map.set(key, [...(map.get(key) ?? []), pipeline]);
    }
    for (const list of map.values()) list.sort((a, b) => a.index - b.index);
    return [...map.entries()].sort(([a], [b]) =>
      a === NO_GROUP ? -1 : b === NO_GROUP ? 1 : a.localeCompare(b, 'pt-BR'),
    );
  }, [filtered]);

  const favorites = filtered.filter((pipeline) => pipeline.isFavorite);

  /** Solta `dragging` antes de `target` (ou no fim do grupo, sem alvo). */
  const drop = (group: string, target: Pipeline | null) => {
    const moving = pipelines.find((pipeline) => pipeline.id === dragging);
    setDragging(null);
    if (!moving || moving.id === target?.id) return;
    const members = pipelines
      .filter((pipeline) => (pipeline.group ?? NO_GROUP) === group && pipeline.id !== moving.id)
      .sort((a, b) => a.index - b.index);
    const at = target ? members.findIndex((pipeline) => pipeline.id === target.id) : -1;
    members.splice(at < 0 ? members.length : at, 0, moving);
    reorder.mutate(
      members.map((pipeline, index) => ({
        id: pipeline.id,
        group: group === NO_GROUP ? null : group,
        index,
      })),
    );
  };

  const item = (pipeline: Pipeline, group: string | null) => (
    <li
      key={`${group ?? 'fav'}-${pipeline.id}`}
      draggable={canManage && group !== null}
      onDragStart={() => setDragging(pipeline.id)}
      onDragEnd={() => setDragging(null)}
      onDragOver={(event) => group !== null && dragging && event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        if (group !== null) drop(group, pipeline);
      }}
      className={`group flex items-center gap-1 rounded-lg pr-1 ${
        pipeline.id === activeId ? 'bg-slate-900 text-white' : 'hover:bg-slate-100'
      } ${dragging === pipeline.id ? 'opacity-40' : ''}`}
    >
      <button
        type="button"
        onClick={() => onSelect(pipeline.id)}
        className="min-w-0 flex-1 truncate px-2 py-1.5 text-left text-sm"
        title={pipeline.name}
      >
        {pipeline.name}
      </button>
      <button
        type="button"
        aria-label={pipeline.isFavorite ? 'Tirar dos favoritos' : 'Favoritar'}
        onClick={() => favorite.mutate({ id: pipeline.id, on: !pipeline.isFavorite })}
        className={`px-1 text-sm ${
          pipeline.isFavorite
            ? 'text-amber-400'
            : 'text-slate-300 opacity-0 group-hover:opacity-100'
        }`}
      >
        {pipeline.isFavorite ? '★' : '☆'}
      </button>
    </li>
  );

  if (hidden) {
    return (
      <aside className="flex w-9 shrink-0 flex-col items-center border-r border-slate-200 bg-white py-3">
        <button
          type="button"
          onClick={() => setUiPrefs({ pipelinesCollapsed: false })}
          className="rounded px-1 text-lg text-slate-500 hover:text-slate-900"
          title="Mostrar as pipelines"
          aria-label="Mostrar as pipelines"
        >
          »
        </button>
      </aside>
    );
  }

  return (
    <aside className="flex w-60 shrink-0 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center gap-1 border-b border-slate-200 p-3">
        <input
          className={`${inputClass} mt-0`}
          placeholder="Buscar pipeline…"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
        />
        <button
          type="button"
          onClick={() => setUiPrefs({ pipelinesCollapsed: true })}
          className="rounded px-1 text-lg text-slate-400 hover:text-slate-900"
          title="Recolher as pipelines"
          aria-label="Recolher as pipelines"
        >
          «
        </button>
      </div>

      <nav className="flex-1 space-y-3 overflow-y-auto p-2">
        {favorites.length > 0 && (
          <div>
            <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Favoritas
            </p>
            <ul className="space-y-0.5">{favorites.map((pipeline) => item(pipeline, null))}</ul>
          </div>
        )}

        {groups.map(([group, list]) => {
          const isCollapsed = collapsed.has(group);
          return (
            <div
              key={group}
              onDragOver={(event) => dragging && event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                drop(group, null);
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setCollapsed((current) => {
                    const next = new Set(current);
                    if (next.has(group)) next.delete(group);
                    else next.add(group);
                    return next;
                  })
                }
                className="flex w-full items-center justify-between px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-600"
              >
                <span className="truncate">{group}</span>
                <span>{isCollapsed ? '▸' : '▾'}</span>
              </button>
              {!isCollapsed && (
                <ul className="space-y-0.5">{list.map((pipeline) => item(pipeline, group))}</ul>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <p className="px-2 py-4 text-center text-xs text-slate-400">Nenhuma pipeline.</p>
        )}
      </nav>

      {canManage && (
        <div className="border-t border-slate-200 p-2">
          <button
            type="button"
            onClick={onCreate}
            className="w-full rounded-lg px-2 py-2 text-left text-sm font-medium text-slate-600 hover:bg-slate-100"
          >
            + Adicionar pipeline
          </button>
        </div>
      )}
    </aside>
  );
}
